import { EnvelopeIcon } from '@heroicons/react/24/outline'
import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, UseQueryArgs } from 'urql'
import { User } from '../../../gql/graphql'
import { APP_ADMIN } from '../../_constants/permissions'
import {
  resetScheduleEmailStatusToCheckinMutation,
  sendScheduleChangeEmailMutation
} from '../../_lib/graphql/mutations'
import { readCurrentSeasonChangedScheduleEmailsQuery } from '../../_lib/graphql/queries'
import { Spinner } from '../../_ui'
import { Badge } from '../../_ui/Badge'
import { Button } from '../../_ui/Button'
import { Dialog } from '../../_ui/Dialog'
import { useSnackbarPush } from '../SnackbarContext/SnackbarContext'
import { useMyself } from '../UserContext'

type ChangedScheduleEmailUsersQueryData = {
  readCurrentSeason?: {
    id: string
    userWithChangedScheduleEmails?: Array<User | null> | null
  } | null
}

export const ScheduleEmailChangedUsersDialog = () => {
  const { pushSuccess, pushError } = useSnackbarPush()
  const [open, setOpen] = useState(false)
  const [sendingUserID, setSendingUserID] = useState<string | null>(null)
  const [resettingUserID, setResettingUserID] = useState<string | null>(null)
  const [, setSearchParams] = useSearchParams()
  const { canAccess } = useMyself() || {}

  const [queryResult, reexecuteQuery] = useQuery({
    query: readCurrentSeasonChangedScheduleEmailsQuery,
  } as UseQueryArgs)
  const [sendScheduleChangeEmailResult, executeSendScheduleChangeEmail] = useMutation(sendScheduleChangeEmailMutation)
  const [resetScheduleEmailStatusResult, executeResetScheduleEmailStatusToCheckin] = useMutation(resetScheduleEmailStatusToCheckinMutation)

  const users = useMemo(() => {
    const season = queryResult.data as ChangedScheduleEmailUsersQueryData | undefined
    return (season?.readCurrentSeason?.userWithChangedScheduleEmails || []).filter(Boolean) as User[]
  }, [queryResult.data])

  const badgeValue = users.length > 99 ? '99+' : `${users.length}`

  const handleUserOpen = useCallback((userID: string) => {
    setOpen(false)
    setSearchParams(prev => {
      prev.set('modal', `user-${userID}`)
      return prev
    })
  }, [setSearchParams])

  const handleSendScheduleChangeEmail = useCallback((userID: string) => {
    setSendingUserID(userID)

    executeSendScheduleChangeEmail({
      input: {
        userID
      }
    }).then(res => {
      if (res.error) {
        pushError(res.error.message)
      } else if (res.data?.sendScheduleChangeEmail) {
        pushSuccess('Schichtplan-Änderungs-E-Mail versendet')
        reexecuteQuery({ requestPolicy: 'network-only' })
      } else {
        pushError('Schichtplan-Änderungs-E-Mail konnte nicht versendet werden')
      }

      setSendingUserID(null)
    })
  }, [executeSendScheduleChangeEmail, pushError, pushSuccess, reexecuteQuery])

  const handleResetScheduleEmailStatusToCheckin = useCallback((userID: string) => {
    setResettingUserID(userID)

    executeResetScheduleEmailStatusToCheckin({
      input: {
        userID
      }
    }).then(res => {
      if (res.error) {
        pushError(res.error.message)
      } else if (res.data?.resetScheduleEmailStatusToCheckin) {
        pushSuccess('Schichtplan-Änderung ignoriert')
        reexecuteQuery({ requestPolicy: 'network-only' })
      } else {
        pushError('Schichtplan-Änderung konnte nicht ignoriert werden')
      }

      setResettingUserID(null)
    })
  }, [executeResetScheduleEmailStatusToCheckin, pushError, pushSuccess, reexecuteQuery])

  if (!canAccess || !canAccess(APP_ADMIN)) {
    return null
  }

  return (
    <>
      <button
        type={'button'}
        onClick={() => setOpen(true)}
        className={'relative flex items-center gap-1 text-sm text-gray-700 transition hover:text-primary-500'}
        aria-label={'Geänderte Schichtplan-E-Mails anzeigen'}
        title={'Geänderte Schichtplan-E-Mails anzeigen'}
      >
        <span className={'relative flex items-center pr-4'}>
          <EnvelopeIcon className={'h-5 w-5'} aria-hidden={'true'} />
          <span className={'absolute -right-3 -top-2'}>
            <Badge color={users.length > 0 ? 'red' : 'default'}>
              {badgeValue}
            </Badge>
          </span>
        </span>
      </button>

      <Dialog
        title={'Geänderte Schichtplan-E-Mails'}
        open={open}
        onClose={() => setOpen(false)}
        className={'sm:max-w-2xl'}
      >
        <div className={'space-y-4'}>
          <p className={'text-sm text-gray-600'}>
            {users.length > 0
              ? `${users.length} Benutzer mit geänderter Schichtplan-Email gefunden.`
              : 'Keine Benutzer mit geänderter Schichtplan-Email gefunden.'}
          </p>

          {queryResult.fetching && !queryResult.data ? (
            <div className={'py-10 flex justify-center'}>
              <Spinner size={'w-8 h-8'} color={'text-gray-400'} />
            </div>
          ) : users.length > 0 ? (
            <ul className={'max-h-[60vh] overflow-y-auto divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white'}>
              {users.map(user => {
                const fullName = `${user.firstName || ''} ${user.surname || ''}`.trim() || 'Unbekannter Benutzer'
                const isSending = sendingUserID === user.id
                const isResetting = resettingUserID === user.id

                return (
                  <li key={user.id} className={'flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between'}>
                    <div className={'min-w-0'}>
                      <button
                        type={'button'}
                        onClick={() => handleUserOpen(user.id)}
                        className={'block w-full truncate text-left text-sm font-medium text-gray-900 hover:text-primary-500'}
                      >
                        {fullName}
                      </button>
                      <div className={'truncate text-xs text-gray-500'}>
                        {user.email || 'Keine E-Mail hinterlegt'}
                      </div>
                    </div>
                    <div className={'flex shrink-0 flex-wrap gap-2 justify-start sm:justify-end'}>
                      <Button
                        size={'small'}
                        color={'primary'}
                        icon={<EnvelopeIcon className={'h-4 w-4'} />}
                        disabled={sendScheduleChangeEmailResult.fetching || isSending}
                        onClick={() => handleSendScheduleChangeEmail(user.id)}
                      >
                        {isSending ? 'Sende...' : 'Senden'}
                      </Button>
                      <Button
                        size={'small'}
                        color={'default'}
                        disabled={resetScheduleEmailStatusResult.fetching || isResetting}
                        onClick={() => handleResetScheduleEmailStatusToCheckin(user.id)}
                      >
                        {isResetting ? 'Ignoriere...' : 'Ignorieren'}
                      </Button>
                    </div>
                  </li>
                )
              })}
            </ul>
          ) : (
            <div className={'rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500'}>
              Keine Einträge vorhanden.
            </div>
          )}
        </div>
      </Dialog>
    </>
  )
}

export default ScheduleEmailChangedUsersDialog
